import { FilesetResolver } from "@mediapipe/tasks-vision";

import { getDeviceInfo } from "@/core/device/deviceInfo/deviceInfo";
import { logger } from "@/core/logging";

// Pin the MediaPipe vision runtime to a known-good version. A drifting CDN
// version would silently change the native graph on any `npm install` without
// a PR, re-exposing the class of native-node failures we just patched around.
export const MEDIAPIPE_VISION_VERSION = "0.10.34";

// Primary CDN for WASM + TaskRunner assets. jsDelivr has the best global
// reliability numbers we've observed, but we still hedge with a fallback so
// a single CDN outage doesn't hard-down /studio.
const JSDELIVR_WASM_URL = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_VISION_VERSION}/wasm`;
const UNPKG_WASM_URL = `https://unpkg.com/@mediapipe/tasks-vision@${MEDIAPIPE_VISION_VERSION}/wasm`;

export const MEDIAPIPE_WASM_URL = JSDELIVR_WASM_URL;
export const MEDIAPIPE_WASM_FALLBACK_URLS = [UNPKG_WASM_URL] as const;

// Pinned face_landmarker model — float16/1 is the variant we validated with.
// Google Storage publishes these at stable URLs; do not swap without re-QA'ing
// `calculateFaceOrientation` landmark indices, which are model-specific.
export const MEDIAPIPE_FACE_LANDMARKER_MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";

// Shared stuck-timeout for MediaPipe-driven guidance flows. Used by both
// `useFaceGuidance` (front-capture positioning) and `useProfileAlignment`
// (profile-capture yaw alignment). When guidance has been active this long
// without the user tripping the relevant threshold, the consumer enables
// manual shutter capture so the user isn't stranded on a disabled button.
// Tuning this in one place avoids the two hooks drifting.
export const MEDIAPIPE_STUCK_TIMEOUT_MS = 10000;

export function prefersMediapipeCpuDelegate(): boolean {
  if (typeof navigator === "undefined") return false;

  const { platform } = getDeviceInfo();
  if (platform === "ios") {
    return true;
  }

  // Android builds the GPU graph fine and only fails later inside
  // `detectForVideo` — `ROI contains NaN` / `CalculatorGraph::Run() failed`. The
  // GPU→CPU fallback in `useFaceLandmarker` init only catches the *constructor*
  // throwing, so it never sees this and the session sits on a loading screen
  // until the detect-failure reinit kicks in. Prod logs had this failure class
  // at 100% Android, across several platforms and both studio flows, so start
  // Android on CPU instead of paying a GPU attempt that reliably fails.
  if (platform === "android") {
    return true;
  }

  const ua = navigator.userAgent;
  const isLikelyDesktopSafari =
    /safari/i.test(ua) && !/chrome|chromium|crios|fxios|edg|opr\//i.test(ua);

  return isLikelyDesktopSafari;
}

export async function resolveVisionTasksFileset(): Promise<
  Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>
> {
  const urls = [MEDIAPIPE_WASM_URL, ...MEDIAPIPE_WASM_FALLBACK_URLS];
  let lastError: unknown = null;
  for (const url of urls) {
    try {
      return await FilesetResolver.forVisionTasks(url);
    } catch (err) {
      lastError = err;
      logger.warn("face_landmarker_wasm_cdn_failed", {
        url,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
  throw lastError instanceof Error ? lastError : new Error("All MediaPipe WASM CDNs failed");
}
